import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { apiClient } from '@/services/api';
import { BasicCheckbox } from '@/components/BasicCheckbox';
import { MainSection } from '@/components/layout/MainSection';
import adminDashboardStyles from '@/components/AdminDashboard/styles.module.scss';
import { useTranslation } from 'react-i18next';
import { SEARCH_INPUT_PROPS } from '@/constants';

const BookmakersTab: React.FC = () => {
  const { t } = useTranslation(['admin', 'common']);
  const { isAuthenticated } = useAuth();

  const [adminBookmakers, setAdminBookmakers] = useState<any[]>([]);
  const [bookmakersLoading, setBookmakersLoading] = useState(false);
  const [bookmakersSearch, setBookmakersSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [bookmakersFilter, setBookmakersFilter] = useState<'all' | 'enabled' | 'disabled'>('all');
  const [bookmakersMessage, setBookmakersMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [togglingBookmakerId, setTogglingBookmakerId] = useState<number | null>(null);

  const fetchAdminBookmakers = useCallback(async () => {
    try {
      setBookmakersLoading(true);
      const params: Record<string, string> = {};
      if (appliedSearch) params.search = appliedSearch;
      if (bookmakersFilter === 'enabled') params.isEnabled = 'true';
      if (bookmakersFilter === 'disabled') params.isEnabled = 'false';
      const response = await apiClient.getAdminBookmakers(params);
      setAdminBookmakers(response.data.data.bookmakers || []);
    } catch (error: any) {
      setBookmakersMessage({ type: 'error', text: error.response?.data?.error || 'Failed to load bookmakers' });
    } finally {
      setBookmakersLoading(false);
    }
  }, [appliedSearch, bookmakersFilter]);

  const handleToggleBookmaker = async (bookmakerId: number, currentEnabled: boolean) => {
    try {
      setTogglingBookmakerId(bookmakerId);
      await apiClient.toggleBookmakerEnabled(bookmakerId, !currentEnabled);
      setAdminBookmakers(prev => prev.map(b =>
        b.id === bookmakerId ? { ...b, is_enabled: !currentEnabled } : b
      ));
    } catch (error: any) {
      setBookmakersMessage({ type: 'error', text: error.response?.data?.error || 'Failed to toggle bookmaker' });
    } finally {
      setTogglingBookmakerId(null);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchAdminBookmakers();
    }
  }, [fetchAdminBookmakers, isAuthenticated]);

  return (
    <MainSection title={t('admin:bookmakers')}>
      <div className={adminDashboardStyles.content}>
        <div className={adminDashboardStyles.contentInner}>
          {bookmakersMessage && (
            <div className={`${adminDashboardStyles.message} ${bookmakersMessage.type === 'success' ? adminDashboardStyles.messageSuccess : adminDashboardStyles.messageError}`}>
              {bookmakersMessage.text}
              <button onClick={() => setBookmakersMessage(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px' }}>x</button>
            </div>
          )}

          <div className={adminDashboardStyles.card}>
            {/* Search */}
            <div style={{ marginBottom: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <input
                type="text"
                placeholder={t('admin:searchBookmakers')}
                autoComplete="off"
                {...SEARCH_INPUT_PROPS}
                value={bookmakersSearch}
                onChange={(e) => setBookmakersSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && setAppliedSearch(bookmakersSearch)}
                className="field"
                style={{ flex: 1, minWidth: '200px' }}
              />
              <button className="ls-btn" onClick={() => setAppliedSearch(bookmakersSearch)}>{t('admin:searchAction')}</button>
            </div>

            {/* Filter tabs */}
            <div className={adminDashboardStyles.filterTabs} style={{ marginBottom: '16px' }}>
              {(['all', 'enabled', 'disabled'] as const).map(f => (
                <button
                  key={f}
                  className={`${adminDashboardStyles.filterTab} ${bookmakersFilter === f ? adminDashboardStyles.filterTabActive : ''}`}
                  onClick={() => setBookmakersFilter(f)}
                >
                  {f === 'all' ? 'Все' : f === 'enabled' ? 'Включены' : 'Выключены'}
                </button>
              ))}
            </div>

            {/* Stats */}
            <p className="theme-text-secondary" style={{ fontSize: '12px', marginBottom: '12px' }}>
              {adminBookmakers.length} bookmakers | {adminBookmakers.filter(b => b.is_enabled).length} enabled
            </p>

            {/* Bookmakers list */}
            {bookmakersLoading ? (
              <div className={adminDashboardStyles.loading}>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--accent-primary)', margin: '0 auto' }} />
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '600px', overflowY: 'auto' }}>
                {adminBookmakers.map(bookmaker => (
                  <div
                    key={bookmaker.id}
                    className="d-flex align-items-center g-12"
                    style={{
                      padding: '8px 12px',
                      borderRadius: '8px',
                      background: bookmaker.is_enabled ? 'var(--bg-status-info, rgba(74,158,255,0.05))' : 'transparent',
                      border: '1px solid var(--border)',
                      opacity: togglingBookmakerId === bookmaker.id ? 0.5 : 1,
                      transition: 'opacity 0.2s'
                    }}
                    title={`ID: ${bookmaker.id} | External ID: ${bookmaker.external_id}`}
                  >
                    <BasicCheckbox
                      id={`bookmaker-${bookmaker.id}`}
                      checked={bookmaker.is_enabled}
                      onChange={() => handleToggleBookmaker(bookmaker.id, bookmaker.is_enabled)}
                      color="green"
                    />
                    <label htmlFor={`bookmaker-${bookmaker.id}`} style={{ cursor: 'pointer', flex: 1, fontSize: '13px' }}>
                      {bookmaker.name}
                    </label>
                    <span className="theme-text-secondary" style={{ fontSize: '11px', flexShrink: 0 }}>
                      ID: {bookmaker.external_id}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </MainSection>
  );
};

export default BookmakersTab;
